import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Progress, Tooltip, Empty } from 'antd';
import { useSelector } from 'react-redux';
import { beeXLogger } from '../../../../helpers/logger';
import { EventRanking } from '../EventRanking';
import { MenuOutlined } from '@ant-design/icons';
import { useMonitoringDataWithInterval } from '../../../../hooks/useMonitoringDataWithInterval';
import { useMonitoringDataFetch } from '../../../../hooks/useMonitoringDataFetch';
import Spinner from '../../../../components/Spinner';

import './index.scss';

const EVENT_FREQUENCY_QUESTION_ID = 288;

const EventFrequency = ({
  isActive = true,
  intervalMs = 0,
  onRegisterReload,
  selectedDate,
}) => {
  const [data, setData] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { namespace } = useSelector((state) => state);
  const loadingTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!isActive) {
      setShowAll(false);
    }
  }, [isActive]);

  const baseFetchData = useMonitoringDataFetch(
    EVENT_FREQUENCY_QUESTION_ID,
    selectedDate,
  );

  const fetchEventFrequencyData = useCallback(async () => {
    if (!namespace) return null;

    try {
      setIsLoading(true);
      const rawData = await baseFetchData();
      const transformedData = rawData.map((item, index) => ({
        id: item.serial,
        index: String(index + 1).padStart(2, '0'),
        name: item.name,
        percent: item?.avgRate || 0,
        total: item.total,
      }));

      setData(transformedData);
      if (isMountedRef.current) {
        setIsLoading(false);
        if (loadingTimerRef.current) {
          clearTimeout(loadingTimerRef.current);
          loadingTimerRef.current = null;
        }
      }
      return transformedData;
    } catch (err) {
      beeXLogger.error(err);
      throw err;
    }
  }, [baseFetchData, namespace]);

  const { reload } = useMonitoringDataWithInterval(
    fetchEventFrequencyData,
    isActive ? intervalMs : 0,
    [namespace, isActive],
  );

  React.useEffect(() => {
    if (onRegisterReload && typeof onRegisterReload === 'function') {
      onRegisterReload(reload);
    }
  }, [reload, onRegisterReload]);

  useEffect(() => {
    if (isActive && isLoading && !loadingTimeout) {
      loadingTimerRef.current = setTimeout(() => {
        if (isMountedRef.current && isLoading) {
          setLoadingTimeout(true);
        }
      }, 30000);
    }

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isActive, isLoading, loadingTimeout]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, []);

  const displayedData = showAll ? data : data.slice(0, 5);

  const content = isLoading ? (
    <>
      <Spinner />
      {loadingTimeout && <Empty description="No data" />}
    </>
  ) : (
    <div className="event-frequency">
      <div className="event-frequency-table">
        {/* <div className="event-frequency-header">
          <div className="col-index">No.</div>
          <div className="col-name">Camera</div>
          <div className="col-rate">Tempered Rate</div>
          <div className="col-total">Total Events</div>
        </div> */}
        <div className="event-frequency-list">
          {displayedData?.length > 0 ? (
            displayedData.map((item) => (
              <div key={item.id} className="event-frequency-item">
                <div className="col-index">{item.index}</div>
                <Tooltip title={item.name}>
                  <div className="col-name">{item.name}</div>
                </Tooltip>
                <div className="col-rate">
                  <Progress
                    percent={item.percent}
                    strokeColor={'#0e9347'}
                    format={(percent) => `${percent}%`}
                  />
                </div>
                <div className="col-total">{item.total}</div>
              </div>
            ))
          ) : (
            <Empty description="No data" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <EventRanking
      headerIcon={<MenuOutlined />}
      headerTitle="Top Event Frequency"
      headerSubTitle="Daily occurrence"
      showAll={showAll}
      onToggleAll={() => setShowAll(!showAll)}
      hasMore={data.length > 5}
    >
      {content}
    </EventRanking>
  );
};

export default EventFrequency;
